<template>
	<div>
		<div class="flex filter_table">
			<div class="flex flex_column w_100">
				<AgItem :columnDefs="columnDefs"
						:rowData="rowData"
						:with-button-group="true"
						:with-tabs="true"
						v-on:sortChanged="sortChanged"
						:loading-warning-text="loadingWarningText"
						:key="loadingWarningText"
				></AgItem>
				<div class="flex flex_between">
					<AllInvestmentsRevolvingPutOptionsLegend/>
					<Pagination
						:totalNum="totalNum"
						:pageSize="pageSize"
						:sizeChange="sizeChange"
						:currentChange="currentChange"
						:currentPage="page"
					></Pagination>
				</div>
			</div>
			<AllInvestmentsRevolvingPutOptionsFilter @apply-filter="applyFilter"/>
			<AllInvestmentsRevolvingPutOptionsAssignmentConfirmationModal ref="confirmationsModal" @confirmed="getList"/>
		</div>
	</div>
</template>
<script>

import {asDecimal, asLocalizedDate, asMoneyAmount} from "@/components/templates/agGrid/formatter/valueFormatters";
import AgItem from "@/components/templates/agGrid/AgItem";
import {enumDef} from "@/common/enumDefs";
import Pagination from "@/components/templates/common/Pagination";
import PaginationParent from "@/components/templates/common/PaginationParent";
import AllInvestmentsRevolvingPutOptionsFilter
	from "@/components/templates/investor/allInvestments/revolving/AllInvestmentsRevolvingPutOptionsFilter";
import {revolvingPutOptionsBuyerApi} from "@/common/api/revolvingPutOptionsBuyerApi";
import AllInvestmentsRevolvingPutOptionsAssignmentConfirmationModal
	from "@/components/templates/investor/allInvestments/revolving/AllInvestmentsRevolvingPutOptionsAssignmentConfirmationModal";
import AllInvestmentsRevolvingPutOptionsLegend
	from "@/components/templates/investor/allInvestments/revolving/AllInvestmentsRevolvingPutOptionsLegend";

export default {
	extends: PaginationParent,
	components: {
		AllInvestmentsRevolvingPutOptionsAssignmentConfirmationModal,
		AllInvestmentsRevolvingPutOptionsFilter,
		AllInvestmentsRevolvingPutOptionsLegend,
		AgItem,
		Pagination
	},
	props: {
		accountId: { type: Number, default: -1 },
		accountsAssigned: { type: Boolean },
	},
	data() {
		return {
			columnDefs: [],
			rowData: null,
			sorting: [],
			filters: [],
			confirmationsModalShown: false
		};
	},
	computed: {
		loadingWarningText() {
			return !this.accountsAssigned ? this.$t('common.noAccountsAssigned') : null
		}
	},
	beforeMount() {
		this.setColumnDefs();
		this.getList();
	},
	methods: {
		setColumnDefs() {
			const cellClass = params => {
				return params.data.toBeConfirmedCount > 0 ? "confirmations_required_row" : "";
			};
			this.columnDefs = [
				{
					headerName: this.$t("common.status"),
					field: "status",
					minWidth: 125,
					cellRendererFramework: "EnumAsText",
					enumDef: enumDef.revolvingPutOptionStatus,
					sortable: true,
				},
				{
					headerName: this.$t("common.debtor"),
					field: "debtorName",
					minWidth: 175,
					nameType: "debtorName",
					idType: "debtorId",
					cellRendererFramework: "CompanyTem",
					sortable: true
				},
				{
					headerName: this.$t("common.supplier"),
					field: "sellerName",
					minWidth: 175,
					nameType: "sellerName",
					idType: "sellerId",
					cellRendererFramework: "CompanyTem",
					sortable: true
				},
				{
					headerName: this.$t("common.contractNumber"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.contractNumber.tooltip"),
					},
					field: "referenceNumber",
					minWidth: 175,
					cellRendererFramework: "RevolvingPutOptionReferenceRenderer",
					cellRendererParams: {
						soldReceivablesFunc: (data, postData) => revolvingPutOptionsBuyerApi.inspection.listReceivables(data.offerId, postData)
					},
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.optionStart"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.optionStart.tooltip"),
					},
					minWidth: 120,
					field: "startDate",
					valueFormatter: asLocalizedDate(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.optionEnd"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.optionEnd.tooltip"),
					},
					minWidth: 120,
					field: "endDate",
					valueFormatter: asLocalizedDate(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.discount"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.discount.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 150,
					field: "discountRate",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asDecimal({ showPercentSign: true }),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.revolvingLine"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.revolvingLine.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 150,
					field: "saleLimit",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asMoneyAmount(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.estimatedSalesVolume"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.estimatedSalesVolume.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 215,
					field: "estimatedSalesVolume",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asMoneyAmount(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.premium"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.premium.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 150,
					field: "premium",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asDecimal({ showPercentSign: true }),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.utilization"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.utilization.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 125,
					field: "usedSaleLimitPercentage",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asDecimal({ showPercentSign: true }),
					sortable: true
				},
				{
					headerName: this.$t("common.actions"),
					minWidth: 100,
					cellRendererFramework: "AllInvestmentsRevolvingPutOptionsTableOperation",
					cellRendererParams: {
						showConfirmations: this.showConfirmations,
						cancelExtraordinary: this.confirmForCancelExtraordinary,
					},
					pinned: "right",
				}
			];
			this.columnDefs.forEach(col => {
				if (col.children) {
					col.children.forEach(childCol => (childCol.cellClass = cellClass));
				} else {
					col.cellClass = cellClass;
				}
			});
		},
		sortChanged(sortData) {
			this.sorting = sortData;
			this.initPage();
			this.getList();
		},
		getList() {
			// Early out if the account is not yet set, showing an empty table
			if (this.accountId < 0) {
				return;
			}

			let postData = {
				columns: [],
				filters: this.filters || [],
				maxResults: this.maxResults,
				offset: this.offset,
				sorting: this.sorting
			};

			revolvingPutOptionsBuyerApi.inspection.listContracts(this.accountId, postData)
				.then(res => {
					this.setTotalNum(res.recordsTotal);
					this.rowData = res.data;
				})
				.catch(this.toast);
		},
		showConfirmations(data) {
			this.$refs.confirmationsModal.showWithOffer(data.offerId);
		},
		confirmForCancelExtraordinary(data) {
			let option = {
				title: this.$t("investor.revolvingPutOptions.cancelExtraordinary.confirm.title"),
				msg: this.$t("investor.revolvingPutOptions.cancelExtraordinary.confirm.msg", {
					TBCareMail: this.$store.getters.careEmail
				}),
				confirmFun: this.cancelOptionExtraordinary,
				params: data
			};
			this.confirmMeg(option, true);
		},
		cancelOptionExtraordinary(data) {
			revolvingPutOptionsBuyerApi.operation.cancelPutOption(data.offerId)
				.then(() => {
					this.toastSuccess();
					this.getList();
				})
				.catch(this.toast);
		},
		applyFilter(filters) {
			this.filters = filters;
			this.getList();
		},
	},
	watch: {
		accountId() {
			this.getList();
		},
	}
};
</script>

<style lang="scss" scoped>
@import "src/components/templates/investor/allInvestments/revolving/allInvestments.scss";
</style>
